import { Col, Flex, Form, Modal, Row, Switch, Tag, Tooltip } from "antd";
import * as Icon from "@ant-design/icons";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../util/hooks";
import { setTitle } from "../../redux/slices/configuracoes/configuracoesSlice";
import { atualizarUsuarioAtivo, buscarUsuario, deletarUsuario } from "../../services/accountService";
import { setBuscaUsuario, setModalAlterarSenhaUsuario, setModalCadastrarUsuario, setModalDeletarUsuario, setModalPermissaoUsuario, setUsuario } from "../../redux/slices/account/accountSlice";
import Search from "antd/lib/input/Search";
import { ColumnsType } from "antd/lib/table";
import { buscarEmpresa } from "../../services/empresaService";
import { AtualizarUsuarioAtivoModel } from "../../models/account/chamadas/atualizarUsuarioAtivoModel";
import { UsuarioModel } from "../../models/account/usuarioModel";
import TableComponent from "../../components/table/tableComponent";
import AcoesComponent from "../../components/table/acoesComponent";
import { ItemMenuAcoes } from "../../models/sistema/itemMenuAcoes";
import MensagemModal from "../../components/modal/mensagem";
import CadastrarUsuarioModal from "./modals/cadastrar";
import PermissoesUsuarioModal from "./modals/permissoes";
import AlterarSenhaUsuarioModal from "./modals/alterarSenha";
import { ButtonComponent } from "../../components/button/cadastrar";
import { SwitchComponent } from "../../components/button/switch";
import { DeletarUsuarioModel } from "../../models/account/chamadas/deletarUsuarioModel";

const Usuario = () => {
  //estados globais
  const accountState = useAppSelector((state) => state.account);
  const empresaState = useAppSelector((state) => state.empresa);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const editar = Object.keys(accountState.usuario).length !== 0;

  useEffect(() => {
    dispatch(setTitle("USUÁRIOS"));
    dispatch(buscarUsuario(accountState.busca));
    dispatch(buscarEmpresa(empresaState.busca));
  }, [dispatch]);

  const goCadastro = () => {
    dispatch(setUsuario({}));
    dispatch(setModalCadastrarUsuario(true));
  };

  const goEditar = (value: UsuarioModel) => {
    dispatch(setUsuario(value));
    dispatch(setModalCadastrarUsuario(true));
  };

  const closeModalCadastro = () => {
    dispatch(setModalCadastrarUsuario(false));
  };

  const goAlterarSenha = (value: UsuarioModel) => {
    dispatch(setUsuario(value));
    dispatch(setModalAlterarSenhaUsuario(true));
  };

  const closeModalAlterarSenha = () => {
    dispatch(setModalAlterarSenhaUsuario(false));
  };

  const goDeletar = (value: UsuarioModel) => {
    dispatch(setUsuario(value));
    dispatch(setModalDeletarUsuario(true));
  };

  const closeModalDeletar = () => {
    dispatch(setModalDeletarUsuario(false));
  };

  const goPermissoes = (value: UsuarioModel) => {
    dispatch(setUsuario(value));
    dispatch(setModalPermissaoUsuario(true));
  };

  const closeModalPermissoes = () => {
    dispatch(setModalPermissaoUsuario(false));
  };

  const onChangeAtivo = async (value: UsuarioModel) => {
    let args: AtualizarUsuarioAtivoModel = {
      idUsuario: value.id,
      ativo: !value.ativo,
    };
    dispatch(atualizarUsuarioAtivo({ value: args, busca: accountState.busca }));
  };

  const onSearch = (value: string) => {
    dispatch(setBuscaUsuario(value));
    dispatch(buscarUsuario(value));
  };

  const filtroEmpresa = () =>
    Array.from(
      new Set<any>(
        empresaState.list.map((a: any) => ({
          text: a.apelido,
          value: a.apelido,
        }))
      )
    );

  const items: ItemMenuAcoes[] = [
    {
      label: "Editar",
      key: "1",
      icon: <Icon.EditOutlined />,
      function: (item: any) => goEditar(item),
    },
    {
      label: "Permissões",
      key: "2",
      icon: <Icon.ControlOutlined />,
      function: (item: any) => goPermissoes(item),
      disabled: (item: any) => item.perfis?.includes("ADMIN"),
    },
    {
      label: "Alterar Senha",
      key: "3",
      icon: <Icon.UserSwitchOutlined />,
      function: (item: any) => goAlterarSenha(item),
    },
    {
      label: "Remover",
      key: "4",
      icon: <Icon.DeleteOutlined />,
      function: (item: any) => goDeletar(item),
    },
  ];

  const columns: ColumnsType<UsuarioModel> = [
    {
      title: "NOME",
      dataIndex: "nome",
      align: "left",
      sorter: (a, b) => a.nome.localeCompare(b.nome),
      sortDirections: ["ascend", "descend", "ascend"],
    },
    {
      title: "EMAIL",
      dataIndex: "email",
      width: 220,
      align: "left",
      sorter: (a, b) => a.email.localeCompare(b.email),
      sortDirections: ["ascend", "descend", "ascend"],
    },
    {
      title: "PERFIL",
      dataIndex: "perfil",
      filters: [
        {
          text: "ADMIN",
          value: "ADMIN" as any,
        },
        {
          text: "AUDITORIA",
          value: "AUDITORIA" as any,
        },
        {
          text: "CAIXA",
          value: "CAIXA" as any,
        },
        {
          text: "CONSIGNATARIO",
          value: "CONSIGNATARIO" as any,
        },
        {
          text: "CONTABILIDADE",
          value: "CONTABILIDADE" as any,
        },
        {
          text: "CORPORATIVO",
          value: "CORPORATIVO" as any,
        },
        {
          text: "DIRETORIA",
          value: "DIRETORIA" as any,
        },
        {
          text: "FINANCEIRO",
          value: "FINANCEIRO" as any,
        },
      ],
      onFilter: (value: any, record: any) => record.perfis?.toString().includes(value),
      render: (_, record) => record.perfis?.map((a: string) => <Tag key={a}>{a}</Tag>),
    },
    {
      title: "EMPRESA",
      dataIndex: "empresas",
      filters: filtroEmpresa(),
      onFilter: (value: any, record: any) => record.empresas?.some((item: any) => item.empresa.includes(value)),
      render: (_, record) => {
        //to-do: transformar em componente
        const maxTagCount = 2;
        const displayedTags = record.empresas?.slice(0, maxTagCount);
        const omittedTags = record.empresas?.slice(maxTagCount);
        return (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {displayedTags?.map((item, index) => (
              <Tag key={index} style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.empresa}
              </Tag>
            ))}
            {(omittedTags?.length ?? 0) > 0 && (
              <Tooltip
                styles={{ root: { pointerEvents: "none" } }}
                title={
                  <ul style={{ paddingLeft: 16, margin: 0 }}>
                    {omittedTags?.map((item, index) => (
                      <li key={index}>{item.empresa}</li>
                    ))}
                  </ul>
                }
              >
                <Tag>{`+ ${omittedTags?.length ?? 0} ...`}</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "ATIVO",
      dataIndex: "ativo",
      align: "center",
      width: 100,
      filters: [
        {
          text: "Ativo",
          value: "true" as any,
        },
        {
          text: "Inativo",
          value: "false" as any,
        },
      ],
      onFilter: (value: any, record: any) => record.ativo.toString().includes(value),
      defaultFilteredValue: ["true" as any],
      render: (_: any, record: any) => <SwitchComponent checked={record.ativo} onChange={() => onChangeAtivo(record)} />,
    },
    {
      title: "AÇÕES",
      dataIndex: "acoes",
      align: "center",
      width: 75,
      render: (_, record) => <AcoesComponent record={record} items={items} />,
    },
  ];

  return (
    <Flex vertical>
      <Form layout="vertical">
        <Row gutter={12} align="bottom">
          <Col span={20}>
            <Flex gap={12}>
              <Form.Item label="Pesquisar">
                <Search allowClear placeholder="Nome ou Email" onSearch={onSearch} className="searchInput" style={{ width: 300 }} enterButton />
              </Form.Item>
            </Flex>
          </Col>
          <Col span={4}>
            <Flex justify="end">
              <ButtonComponent onClick={goCadastro} />
            </Flex>
          </Col>
        </Row>
      </Form>
      <TableComponent data={accountState.usuarios} columns={columns} loading={accountState.loading} />
      <Modal title={editar ? "EDITAR USUÁRIO" : "NOVO USUÁRIO"} centered width={900} open={accountState.modalCadastrar} onCancel={closeModalCadastro} destroyOnHidden footer={null}>
        <CadastrarUsuarioModal />
      </Modal>
      <Modal title={"PERMISSÕES USUÁRIO"} centered width={450} open={accountState.modalPermissao} onCancel={closeModalPermissoes} footer={null} destroyOnHidden>
        <PermissoesUsuarioModal />
      </Modal>
      <Modal title={"ALTERAR SENHA USUÁRIO"} centered width={350} open={accountState.modalAlterarSenha} onCancel={closeModalAlterarSenha} footer={null} destroyOnHidden>
        <AlterarSenhaUsuarioModal origem="usuario" />
      </Modal>
      <Modal title={"REMOVER USUÁRIO"} centered width={350} open={accountState.modalDeletar} onCancel={closeModalDeletar} footer={null} destroyOnHidden>
        <MensagemModal
          text="Deseja remover o usuário?"
          action={deletarUsuario({ value: { idUsuario: accountState.usuario.id, perfil: accountState.usuario.perfis } as DeletarUsuarioModel, busca: accountState.busca })}
          close={setModalDeletarUsuario(false)}
        />
      </Modal>
    </Flex>
  );
};

export default Usuario;
